import Grid from "@mui/material/Grid";
import find from "lodash/find";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { Typography } from "@mui/material";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { DatePicker } from "components/DatePicker";
import { Autocomplete } from "components/Autocomplete";
import { FormSectionTitle } from "components/AlertsManagement/Case/form/FormSectionTitle";
import { MultilineTextField } from "components/MultilineTextField";

export const CaseDetailsForm = ({
  formik,
  site,
  siteLoading,
  siteError,
  machine,
  machineLoading,
  machineError,
  componentId,
  components,
  componentsLoading,
  componentsError,
  onChangeComponent = () => {}
}) => {
  useEffect(() => {
    const component = find(components, ({ id }) => id === componentId) ?? null;

    formik.setFieldValue("component", component);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentId, components]);

  const handleOnComponentChange = (_evt, value) => {
    formik.setFieldValue("component", value ?? null);
    formik.setFieldValue("estimated_saving", value?.maintenance_cost ?? null);
    onChangeComponent(value);
  };

  return (
    <ProgressIndicatorContainer
      loading={siteLoading || machineLoading || componentsLoading}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormSectionTitle variant="h5">Case details</FormSectionTitle>
        </Grid>
        <Grid item xs={12} sm={4}>
          <DatePicker
            id="open_date"
            name="open_date"
            label="Open Date"
            date={formik.values.open_date}
            onChange={date => formik.setFieldValue("open_date", date)}
            ariaLabel="diagnostic report date"
            disableFuture
            slotProps={{
              textField: {
                id: "open_date",
                onBlur: formik.handleBlur,
                error:
                  formik.touched.open_date && Boolean(formik.errors.open_date),
                helperText: formik.touched.open_date && formik.errors.open_date
              }
            }}
          />
        </Grid>
        <Grid item xs={12}>
          {!siteError ? (
            <Typography>Site: {site?.name ?? ""}</Typography>
          ) : (
            <Typography color="error">Site failed to load</Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          {!machineError ? (
            <Typography>Machine: {machine?.name ?? ""}</Typography>
          ) : (
            <Typography color="error">Machine failed to load</Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          {!componentsError ? (
            <Autocomplete
              id="component"
              name="component"
              label="Component"
              required
              loading={componentsLoading}
              options={components ?? []}
              getOptionLabel={option => option.name}
              value={formik.values.component}
              onChange={handleOnComponentChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.component && Boolean(formik.errors.component)
              }
              helperText={formik.touched.component && formik.errors.component}
            />
          ) : (
            <Typography color="error">Components failed to load</Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <MultilineTextField
            id="description"
            name="description"
            label="Description"
            required
            fullWidth
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={formik.touched.description && formik.errors.description}
          />
        </Grid>
      </Grid>
    </ProgressIndicatorContainer>
  );
};

CaseDetailsForm.propTypes = {
  formik: PropTypes.object,
  site: PropTypes.object,
  siteLoading: PropTypes.bool,
  siteError: PropTypes.bool,
  selectSite: PropTypes.bool,
  machine: PropTypes.object,
  machineLoading: PropTypes.bool,
  machineError: PropTypes.bool,
  componentId: PropTypes.number,
  components: PropTypes.array,
  componentsLoading: PropTypes.bool,
  componentsError: PropTypes.bool,
  onChangeComponent: PropTypes.func
};
